<script setup lang="ts">
/**
 * The settings of a table's look, as controls.
 *
 * One set of controls for two places: a table's inspector, where they show the
 * look in force and mark what this table has adjusted from its style, and the
 * library's editor, where they edit a style and nothing is adjusted. It holds
 * no look of its own: it is given one, says what was changed, and leaves what
 * to do with the change to whoever put it there.
 *
 * Every choice starts at the document's, which is what "Document" means in each
 * list. A setting adjusted on a table shows a way back to the style it follows.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { Segmented, Switch, type SegmentedOption } from '@/components/ui'
import {
  ACCENT_PRESET_IDS,
  ACCENT_PRESETS,
  TABLE_DENSITY_IDS,
  TABLE_RULE_IDS,
} from '@/domain/layout'
import InspectorField from '@/sections/InspectorField.vue'
import CellColourPicker from '@/sections/table/CellColourPicker.vue'
import {
  TABLE_HEAD_CASES,
  TABLE_HEADS,
  TABLE_NEGATIVES,
  TABLE_TOTALS,
  type TableLook,
} from '@/sections/table/look'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const report = useReportStore()

const props = withDefaults(
  defineProps<{
    /** The look in force, which is what the controls show. */
    look: TableLook
    /** The settings this table sets differently from its style, if it follows one. */
    adjusted?: readonly (keyof TableLook)[]
  }>(),
  { adjusted: () => [] },
)

const emit = defineEmits<{
  /** A setting changed, to a value or to nothing (back to the document's). */
  set: [key: keyof TableLook, value: TableLook[keyof TableLook] | undefined]
  /** A setting taken back to what the style says. */
  revert: [key: keyof TableLook]
}>()

const DOCUMENT = 'document'

function isAdjusted(key: keyof TableLook): boolean {
  return props.adjusted.includes(key)
}

const heads = computed<SegmentedOption[]>(() =>
  TABLE_HEADS.map((value) => ({ value, label: t(`inspector.table.look.heads.${value}`) })),
)
const head = computed(() => props.look.head ?? 'underline')

function chooseHead(value: string | undefined): void {
  const chosen = TABLE_HEADS.find((option) => option === value)
  // The underline is the house head: choosing it is going back to the default.
  emit('set', 'head', chosen === 'underline' ? undefined : chosen)
}

const colours = computed(() =>
  ACCENT_PRESET_IDS.map((id) => ({
    id,
    colour: ACCENT_PRESETS[id],
    name: t(`document.accents.${id}`),
  })),
)

const lines = computed<SegmentedOption[]>(() => [
  { value: DOCUMENT, label: t('inspector.table.look.document') },
  ...TABLE_RULE_IDS.map((value) => ({ value, label: t(`document.tableRules.${value}`) })),
])

function chooseRules(value: string | undefined): void {
  emit(
    'set',
    'rules',
    TABLE_RULE_IDS.find((option) => option === value),
  )
}

const rows = computed<SegmentedOption[]>(() => [
  { value: DOCUMENT, label: t('inspector.table.look.document') },
  ...TABLE_DENSITY_IDS.map((value) => ({ value, label: t(`document.tableDensities.${value}`) })),
])

function chooseDensity(value: string | undefined): void {
  emit(
    'set',
    'density',
    TABLE_DENSITY_IDS.find((option) => option === value),
  )
}

const headCases = computed<SegmentedOption[]>(() =>
  TABLE_HEAD_CASES.map((value) => ({ value, label: t(`inspector.table.look.headCases.${value}`) })),
)

function chooseHeadCase(value: string | undefined): void {
  const chosen = TABLE_HEAD_CASES.find((option) => option === value)
  // The words as written are how a head is drawn unless it says otherwise.
  emit('set', 'headCase', chosen === 'upper' ? chosen : undefined)
}

const totals = computed<SegmentedOption[]>(() => [
  { value: DOCUMENT, label: t('inspector.table.look.document') },
  ...TABLE_TOTALS.map((value) => ({
    value,
    label: t(`inspector.table.look.totalsStyles.${value}`),
  })),
])

function chooseTotals(value: string | undefined): void {
  emit(
    'set',
    'totals',
    TABLE_TOTALS.find((option) => option === value),
  )
}

const negatives = computed<SegmentedOption[]>(() =>
  TABLE_NEGATIVES.map((value) => ({
    value,
    label: t(`inspector.table.look.negativeStyles.${value}`),
  })),
)

function chooseNegatives(value: string | undefined): void {
  const chosen = TABLE_NEGATIVES.find((option) => option === value)
  // The sign is how a figure is held: choosing it is going back to the default.
  emit('set', 'negatives', chosen === 'parentheses' ? chosen : undefined)
}

const banded = computed(() => props.look.banded ?? report.layout.tableZebra)
</script>

<template>
  <div class="controls">
    <div class="row" :class="{ 'row--adjusted': isAdjusted('head') }">
      <InspectorField :label="t('inspector.table.look.head')">
        <Segmented
          :model-value="head"
          :options="heads"
          size="sm"
          block
          :label="t('inspector.table.look.head')"
          @update:model-value="chooseHead"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('head')"
        type="button"
        class="revert"
        @click="emit('revert', 'head')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('headCase') }">
      <InspectorField :label="t('inspector.table.look.headCase')">
        <Segmented
          :model-value="look.headCase ?? 'normal'"
          :options="headCases"
          size="sm"
          block
          :label="t('inspector.table.look.headCase')"
          @update:model-value="chooseHeadCase"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('headCase')"
        type="button"
        class="revert"
        @click="emit('revert', 'headCase')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('colour') }">
      <CellColourPicker
        :label="t('inspector.table.look.colour')"
        :options="colours"
        :chosen="look.colour"
        :none-label="t('inspector.table.look.documentColour')"
        :custom-label="t('inspector.table.look.customColour')"
        stacked
        @choose="emit('set', 'colour', $event ?? undefined)"
      />
      <button
        v-if="isAdjusted('colour')"
        type="button"
        class="revert"
        @click="emit('revert', 'colour')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('tint') }">
      <CellColourPicker
        :label="t('inspector.table.look.tint')"
        :options="colours"
        :chosen="look.tint"
        :none-label="t('inspector.table.look.derivedTint')"
        :custom-label="t('inspector.table.look.customColour')"
        stacked
        @choose="emit('set', 'tint', $event ?? undefined)"
      />
      <button
        v-if="isAdjusted('tint')"
        type="button"
        class="revert"
        @click="emit('revert', 'tint')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('rules') }">
      <InspectorField :label="t('inspector.table.look.lines')">
        <Segmented
          :model-value="look.rules ?? DOCUMENT"
          :options="lines"
          size="sm"
          block
          :label="t('inspector.table.look.lines')"
          @update:model-value="chooseRules"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('rules')"
        type="button"
        class="revert"
        @click="emit('revert', 'rules')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('density') }">
      <InspectorField :label="t('inspector.table.look.rows')">
        <Segmented
          :model-value="look.density ?? DOCUMENT"
          :options="rows"
          size="sm"
          block
          :label="t('inspector.table.look.rows')"
          @update:model-value="chooseDensity"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('density')"
        type="button"
        class="revert"
        @click="emit('revert', 'density')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('totals') }">
      <InspectorField
        :label="t('inspector.table.look.totals')"
        :hint="t('inspector.table.look.totalsHint')"
      >
        <Segmented
          :model-value="look.totals ?? DOCUMENT"
          :options="totals"
          size="sm"
          block
          :label="t('inspector.table.look.totals')"
          @update:model-value="chooseTotals"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('totals')"
        type="button"
        class="revert"
        @click="emit('revert', 'totals')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="row" :class="{ 'row--adjusted': isAdjusted('negatives') }">
      <InspectorField
        :label="t('inspector.table.look.negatives')"
        :hint="t('inspector.table.look.negativesHint')"
      >
        <Segmented
          :model-value="look.negatives ?? 'minus'"
          :options="negatives"
          size="sm"
          block
          :label="t('inspector.table.look.negatives')"
          @update:model-value="chooseNegatives"
        />
      </InspectorField>
      <button
        v-if="isAdjusted('negatives')"
        type="button"
        class="revert"
        @click="emit('revert', 'negatives')"
      >
        {{ t('inspector.table.style.revert') }}
      </button>
    </div>

    <div class="switches">
      <div class="row" :class="{ 'row--adjusted': isAdjusted('banded') }">
        <Switch :model-value="banded" @update:model-value="emit('set', 'banded', $event)">
          {{ t('inspector.table.look.banded') }}
        </Switch>
        <button
          v-if="isAdjusted('banded')"
          type="button"
          class="revert"
          @click="emit('revert', 'banded')"
        >
          {{ t('inspector.table.style.revert') }}
        </button>
      </div>
      <div class="row" :class="{ 'row--adjusted': isAdjusted('labels') }">
        <Switch
          :model-value="look.labels ?? false"
          @update:model-value="emit('set', 'labels', $event || undefined)"
        >
          {{ t('inspector.table.look.labels') }}
        </Switch>
        <button
          v-if="isAdjusted('labels')"
          type="button"
          class="revert"
          @click="emit('revert', 'labels')"
        >
          {{ t('inspector.table.style.revert') }}
        </button>
      </div>
      <div class="row" :class="{ 'row--adjusted': isAdjusted('total') }">
        <Switch
          :model-value="look.total ?? false"
          @update:model-value="emit('set', 'total', $event || undefined)"
        >
          {{ t('inspector.table.look.total') }}
        </Switch>
        <button
          v-if="isAdjusted('total')"
          type="button"
          class="revert"
          @click="emit('revert', 'total')"
        >
          {{ t('inspector.table.style.revert') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.controls {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.switches {
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.row {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

/* A setting this table sets differently from its style: marked, with the way back under it. */
.row--adjusted {
  padding-left: var(--sp-4);
  border-left: 2px solid var(--accent);
}

.revert {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font-size: var(--text-xs);
  cursor: pointer;
}

.revert:hover {
  text-decoration: underline;
}
</style>
